import React, { useState, useEffect } from 'react'
import { 
  DollarSign,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  Filter,
  Download,
  Receipt,
  Users,
  Building,
  Briefcase
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import costReportsService from '../../services/costReportsService'
import DataTable from '../../components/DataTable/DataTable'

const CostReports = () => {
  const navigate = useNavigate()
  const [reportsData, setReportsData] = useState(null)
  const [filterOptions, setFilterOptions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    category: '',
    supplier: '',
    department: '',
    project: ''
  })

  useEffect(() => {
    loadFilterOptions()
    loadReportsData()
  }, [])

  useEffect(() => {
    loadReportsData()
  }, [filters])

  const loadFilterOptions = async () => {
    try {
      const response = await costReportsService.getCostFilterOptions()
      if (response.success) {
        setFilterOptions(response.data)
        // Set default date range (last 12 months)
        const endDate = new Date()
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)
        setFilters(prev => ({
          ...prev,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }))
      }
    } catch (error) {
      console.error('Error loading filter options:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load filter options')
      }
    }
  }

  const loadReportsData = async () => {
    try {
      setLoading(true)
      const response = await costReportsService.getCostReports(filters)
      if (response.success) {
        setReportsData(response.data)
      } else {
        toast.error(response.message || 'Failed to load cost reports')
      }
    } catch (error) {
      console.error('Error loading cost reports:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load cost reports')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleResetFilters = () => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 12)
    setFilters({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      category: '',
      supplier: '',
      department: '',
      project: ''
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0)
  }

  const formatPercentage = (num) => {
    return `${Math.round(num * 100) / 100}%`
  }

  // Category Cost Analysis Table Columns
  const categoryColumns = [
    {
      accessorKey: 'categoryName',
      header: 'Category',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left', fontWeight: '500' }}>
          {getValue() || 'Uncategorized'}
        </div>
      ),
    },
    {
      accessorKey: 'invoiceCount',
      header: 'Invoices',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalQuantity',
      header: 'Total Quantity',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalCost',
      header: 'Total Cost',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalTax',
      header: 'Total Tax',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalDiscount',
      header: 'Total Discount',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', color: '#28a745' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'averageUnitPrice',
      header: 'Avg. Unit Price',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  // Supplier Cost Analysis Table Columns
  const supplierColumns = [
    {
      accessorKey: 'supplierName',
      header: 'Supplier',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.supplierCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.supplierCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'invoiceCount',
      header: 'Invoices',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalCost',
      header: 'Total Cost',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalPaid',
      header: 'Total Paid',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', color: '#28a745' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalOutstanding',
      header: 'Outstanding',
      cell: ({ getValue }) => {
        const amount = getValue() || 0
        return (
          <div style={{ textAlign: 'right', fontWeight: '600', color: amount > 0 ? '#dc3545' : '#28a745' }}>
            {formatCurrency(amount)}
          </div>
        )
      },
    },
    {
      accessorKey: 'averageInvoiceValue',
      header: 'Avg. Invoice Value',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  // Department Cost Analysis Table Columns
  const departmentColumns = [
    {
      accessorKey: '_id',
      header: 'Department',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left', fontWeight: '500' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'invoiceCount',
      header: 'Invoices',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalCost',
      header: 'Total Cost',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalPaid',
      header: 'Total Paid',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', color: '#28a745' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalOutstanding',
      header: 'Outstanding',
      cell: ({ getValue }) => {
        const amount = getValue() || 0
        return (
          <div style={{ textAlign: 'right', fontWeight: '600', color: amount > 0 ? '#dc3545' : '#28a745' }}>
            {formatCurrency(amount)}
          </div>
        )
      },
    },
  ]

  // Project Cost Analysis Table Columns
  const projectColumns = [
    {
      accessorKey: 'projectName',
      header: 'Project',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left', fontWeight: '500' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'invoiceCount',
      header: 'Invoices',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalCost',
      header: 'Total Cost',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalPaid',
      header: 'Total Paid',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', color: '#28a745' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalOutstanding',
      header: 'Outstanding',
      cell: ({ getValue }) => {
        const amount = getValue() || 0
        return (
          <div style={{ textAlign: 'right', fontWeight: '600', color: amount > 0 ? '#dc3545' : '#28a745' }}>
            {formatCurrency(amount)}
          </div>
        )
      },
    },
  ]

  // Top Cost Items Table Columns
  const topCostItemsColumns = [
    {
      accessorKey: 'itemName',
      header: 'Item',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.itemCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.itemCode}</div>
          )}
          <div style={{ fontSize: '12px', color: '#222448' }}>
            {row.original.itemType}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'invoiceCount',
      header: 'Invoices',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalQuantity',
      header: 'Total Quantity',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalCost',
      header: 'Total Cost',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'averageUnitPrice',
      header: 'Avg. Unit Price',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Reports', path: '/procurement?tab=reports' },
    { label: 'Cost Reports', isCurrent: true }
  ]

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Cost Reports</h1>
          <p>Cost analysis and budget tracking</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter size={18} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Filters</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Category
            </label>
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                background: '#fff'
              }}
            >
              <option value="">All Categories</option>
              {filterOptions?.categories?.map(cat => (
                <option key={cat._id} value={cat._id}>
                  {cat.categoryName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Supplier
            </label>
            <select
              name="supplier"
              value={filters.supplier}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                background: '#fff'
              }}
            >
              <option value="">All Suppliers</option>
              {filterOptions?.suppliers?.map(supplier => (
                <option key={supplier._id} value={supplier._id}>
                  {supplier.supplierName} {supplier.supplierCode ? `(${supplier.supplierCode})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading cost reports...</p>
        </div>
      ) : reportsData ? (
        <>
          <div className="dashboard-metrics-grid">
            <div className="metric-card deals">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>Total</span>
                </div>
              </div>
              <div className="metric-value">{formatCurrency(reportsData.overallCostStats.totalCost)}</div>
              <div className="metric-title">Total Cost</div>
              <div className="metric-subtitle">All procurement cost</div>
            </div>

            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Receipt size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>Paid</span>
                </div>
              </div>
              <div className="metric-value">{formatCurrency(reportsData.overallCostStats.totalPaid)}</div>
              <div className="metric-title">Total Paid</div>
              <div className="metric-subtitle">Settled amount</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <TrendingDown size={16} />
                </div>
                <div className="metric-trend negative">
                  <span>Due</span>
                </div>
              </div>
              <div className="metric-value">{formatCurrency(reportsData.overallCostStats.totalOutstanding)}</div>
              <div className="metric-title">Outstanding</div>
              <div className="metric-subtitle">Amount pending</div>
            </div>

            <div className="metric-card conversion">
              <div className="metric-header">
                <div className="metric-icon">
                  <PieChart size={16} />
                </div>
                <div className="metric-trend neutral">
                  <span>Tax</span>
                </div>
              </div>
              <div className="metric-value">{formatCurrency(reportsData.overallCostStats.totalTax)}</div>
              <div className="metric-title">Total Tax</div>
              <div className="metric-subtitle">Tax component</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <TrendingUp size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>Savings</span>
                </div>
              </div>
              <div className="metric-value">{formatCurrency(reportsData.overallCostStats.totalDiscount)}</div>
              <div className="metric-title">Total Discount</div>
              <div className="metric-subtitle">Discounts applied</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <BarChart3 size={16} />
                </div>
                <div className="metric-trend neutral">
                  <span>Average</span>
                </div>
              </div>
              <div className="metric-value">{formatCurrency(reportsData.overallCostStats.averageInvoiceValue)}</div>
              <div className="metric-title">Avg. Invoice Value</div>
              <div className="metric-subtitle">Per invoice</div>
            </div>
          </div>

          {/* Cost Trends Chart Placeholder */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Cost Trends (Last 12 Months)</h3>
            <div style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f9fafb',
              borderRadius: '6px',
              color: 'var(--color-text-secondary, #5B6478)'
            }}>
              Chart visualization would be displayed here
              <br />
              <small>Monthly cost, paid, and outstanding trends</small>
            </div>
          </div>

          {/* Category Cost Analysis */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Category-wise Cost Analysis</h3>
            <DataTable
              data={reportsData.categoryCostAnalysis || []}
              columns={categoryColumns}
              loading={false}
              enableSelection={false}
              noCard={true}
              pageSize={10}
            />
          </div>

          {/* Supplier Cost Analysis */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Top 10 Suppliers by Cost</h3>
            <DataTable
              data={reportsData.supplierCostAnalysis || []}
              columns={supplierColumns}
              loading={false}
              enableSelection={false}
              noCard={true}
              pageSize={10}
            />
          </div>

          {/* Department Cost Analysis */}
          {reportsData.departmentCostAnalysis && reportsData.departmentCostAnalysis.length > 0 && (
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Department-wise Cost Analysis</h3>
              <DataTable
                data={reportsData.departmentCostAnalysis}
                columns={departmentColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                pageSize={10}
              />
            </div>
          )}

          {/* Project Cost Analysis */}
          {reportsData.projectCostAnalysis && reportsData.projectCostAnalysis.length > 0 && (
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Top 10 Projects by Cost</h3>
              <DataTable
                data={reportsData.projectCostAnalysis}
                columns={projectColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                pageSize={10}
              />
            </div>
          )}

          {/* Top Cost Items */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Top 10 Cost Items</h3>
            <DataTable
              data={reportsData.topCostItems || []}
              columns={topCostItemsColumns}
              loading={false}
              enableSelection={false}
              noCard={true}
              pageSize={10}
            />
          </div>

          {/* Tax Analysis */}
          {reportsData.taxAnalysis && (
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Tax Analysis</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '8px' }}>Total CGST</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                    {formatCurrency(reportsData.taxAnalysis.totalCGST)}
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '8px' }}>Total SGST</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                    {formatCurrency(reportsData.taxAnalysis.totalSGST)}
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '8px' }}>Total IGST</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                    {formatCurrency(reportsData.taxAnalysis.totalIGST)}
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '8px' }}>Total Tax</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                    {formatCurrency(reportsData.taxAnalysis.totalTax)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Status Analysis */}
          {reportsData.paymentStatusAnalysis && reportsData.paymentStatusAnalysis.length > 0 && (
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Payment Status Analysis</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                {reportsData.paymentStatusAnalysis.map((status, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '16px',
                      background: '#f9fafb',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}
                  >
                    <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '8px' }}>{status._id || 'Unknown'}</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)', marginBottom: '4px' }}>
                      {formatNumber(status.count)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>
                      {formatCurrency(status.totalAmount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary, #5B6478)' }}>
          No data available
        </div>
      )}
    </div>
  )
}

export default CostReports

